export type PwaInstallState = 'installed' | 'available' | 'manual'
export type PwaInstallOutcome = 'accepted' | 'dismissed' | 'unavailable' | 'installed'

export type PwaInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

type PwaInstallController = {
  capture: (event: PwaInstallPromptEvent) => void
  markInstalled: () => void
  state: () => PwaInstallState
  request: () => Promise<PwaInstallOutcome>
}

export function createPwaInstallController(
  isInstalled: () => boolean,
  announce: () => void,
): PwaInstallController {
  let promptEvent: PwaInstallPromptEvent | null = null
  let installed = isInstalled()

  return {
    capture(event) {
      event.preventDefault()
      promptEvent = event
      announce()
    },
    markInstalled() {
      installed = true
      promptEvent = null
      announce()
    },
    state() {
      if (installed || isInstalled()) return 'installed'
      return promptEvent ? 'available' : 'manual'
    },
    async request() {
      if (installed || isInstalled()) return 'installed'
      if (!promptEvent) return 'unavailable'

      const currentPrompt = promptEvent
      promptEvent = null
      announce()
      await currentPrompt.prompt()
      const choice = await currentPrompt.userChoice
      if (choice.outcome === 'accepted') installed = true
      announce()
      return choice.outcome
    },
  }
}

function isStandalone(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
  const navigatorStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true
  const displayModeStandalone = typeof window.matchMedia === 'function'
    && window.matchMedia('(display-mode: standalone)').matches
  return navigatorStandalone || displayModeStandalone
}

function announceInstallState() {
  window.dispatchEvent(new CustomEvent('repinu:install-state'))
}

const installController = createPwaInstallController(isStandalone, announceInstallState)
let installListenersAttached = false

export function getPwaInstallState(): PwaInstallState {
  return installController.state()
}

export function requestPwaInstall(): Promise<PwaInstallOutcome> {
  return installController.request()
}

export function manualPwaInstallMessage(): string {
  if (typeof navigator !== 'undefined' && /iPad|iPhone|iPod/i.test(navigator.userAgent)) {
    return 'V Safari klepni na Sdílet a potom na Přidat na plochu.'
  }

  return 'V nabídce prohlížeče zvol Nainstalovat aplikaci nebo Přidat na plochu.'
}

function registerPwaInstallPrompt() {
  if (installListenersAttached || typeof window === 'undefined') return
  installListenersAttached = true
  window.addEventListener('beforeinstallprompt', (event) => {
    installController.capture(event as PwaInstallPromptEvent)
  })
  window.addEventListener('appinstalled', () => installController.markInstalled())
}

async function cacheCurrentShell(registration: ServiceWorkerRegistration): Promise<void> {
  const worker = registration.active
  if (!worker) return
  const urls = [...document.querySelectorAll<HTMLScriptElement | HTMLLinkElement>('script[src],link[rel="stylesheet"][href]')]
    .map((element) => element instanceof HTMLScriptElement ? element.src : element.href)
    .filter((value) => new URL(value, window.location.origin).origin === window.location.origin)
  if (urls.length === 0) return

  await new Promise<void>((resolve) => {
    const channel = new MessageChannel()
    const timeout = window.setTimeout(resolve, 5_000)
    channel.port1.onmessage = () => {
      window.clearTimeout(timeout)
      resolve()
    }
    worker.postMessage({ type: 'CACHE_CURRENT_SHELL', urls }, [channel.port2])
  })
}

export function createControllerChangeHandler(initiallyControlled: boolean, reload: () => void) {
  let controllerSeen = initiallyControlled
  let refreshing = false
  return () => {
    if (!controllerSeen) {
      controllerSeen = true
      return
    }
    if (refreshing) return
    refreshing = true
    reload()
  }
}

export function registerPwa() {
  registerPwaInstallPrompt()
  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return

  navigator.serviceWorker.addEventListener(
    'controllerchange',
    createControllerChangeHandler(Boolean(navigator.serviceWorker.controller), () => window.location.reload()),
  )

  window.addEventListener('load', async () => {
    const registration = await navigator.serviceWorker.register('/sw.js')
    const ready = await navigator.serviceWorker.ready
    await cacheCurrentShell(ready)
    if (registration.waiting) window.dispatchEvent(new CustomEvent('repinu:update-ready', { detail: registration }))

    registration.addEventListener('updatefound', () => {
      const worker = registration.installing
      worker?.addEventListener('statechange', () => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller) {
          window.dispatchEvent(new CustomEvent('repinu:update-ready', { detail: registration }))
        }
      })
    })
  })
}